import { create } from 'zustand'
import { supabase, User, AuthState } from '../services/supabase'

interface AuthStore extends AuthState {
  signUp: (email: string, password: string) => Promise<{ success: boolean; needsEmailConfirmation?: boolean }>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updateProfile: (updates: Partial<User>) => Promise<void>
  initialize: () => Promise<void>
  resendConfirmationEmail: (email: string) => Promise<void>
  signInWithProvider: (provider: 'google') => Promise<void>
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  loading: true,
  error: null,

  signUp: async (email: string, password: string) => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })
      
      if (error) throw error
      
      // 检查是否需要邮箱确认
      if (data.user && !data.session) {
        // 用户已创建但需要邮箱确认
        set({ loading: false })
        return { success: true, needsEmailConfirmation: true }
      } else if (data.user && data.session) {
        // 用户已创建且已自动登录（邮箱确认已启用）
        set({ user: data.user, loading: false })
        return { success: true }
      } else {
        set({ loading: false })
        return { success: true, needsEmailConfirmation: true }
      }
    } catch (error: any) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  signIn: async (email: string, password: string) => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) throw error
      
      if (data.user) {
        set({ user: data.user, loading: false })
      }
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  signOut: async () => {
    set({ loading: true })
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      set({ user: null, loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  resetPassword: async (email: string) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email)
      if (error) throw error
      set({ loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  updateProfile: async (updates: Partial<User>) => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: updates
      })
      
      if (error) throw error
      
      if (data.user) {
        set({ user: data.user, loading: false })
      }
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  initialize: async () => {
    set({ loading: true })
    try {
      // 获取当前会话
      const { data: { session } } = await supabase.auth.getSession()
      
      if (session?.user) {
        set({ user: session.user, loading: false })
      } else {
        set({ loading: false })
      }

      // 监听认证状态变化
      supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          set({ user: session.user, loading: false })
        } else if (event === 'SIGNED_OUT') {
          set({ user: null, loading: false })
        }
      })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  resendConfirmationEmail: async (email: string) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      })
      
      if (error) throw error
      set({ loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  signInWithProvider: async (provider: 'google') => {
    set({ loading: true, error: null })
    try {
      // 使用环境变量或当前域名作为重定向URL
      const redirectUrl = import.meta.env.VITE_SITE_URL 
        ? `${import.meta.env.VITE_SITE_URL}/auth/callback`
        : `${window.location.origin}/auth/callback`
      
      // 调试输出
      console.log('OAuth redirectUrl:', redirectUrl)
      console.log('VITE_SITE_URL:', import.meta.env.VITE_SITE_URL)
      console.log('window.location.origin:', window.location.origin)
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl
        }
      })
      
      if (error) throw error
      
      // OAuth流程会重定向到提供商页面，这里不需要设置用户状态
      // 用户状态会在回调页面中处理
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },
})) 